import { getPropertyList } from "lib/propertyList";

export enum PricingTypeId {
  PER_HEAD = 1,
  PER_KILO = 2,
  GROSS = 3,
}

export const ALL_PRICING_TYPES = [
  PricingTypeId.PER_HEAD,
  PricingTypeId.PER_KILO,
  PricingTypeId.GROSS,
];

export const LIVESTOCK_SALE_PRICING_TYPES = [
  PricingTypeId.PER_HEAD,
  PricingTypeId.PER_KILO,
  PricingTypeId.GROSS,
];

export const CLEARING_SALE_PRICING_TYPES = [
  PricingTypeId.PER_HEAD,
  PricingTypeId.GROSS,
];

export const LivestockSalePricingTypeDisplayNameLkp = {
  [PricingTypeId.PER_HEAD]: "$/Head",
  [PricingTypeId.PER_KILO]: "¢/kg",
  [PricingTypeId.GROSS]: "$/Gross",
} as const satisfies Record<PricingTypeId, string>;

export const ClearingSalePricingTypeDisplayNameLkp = {
  [PricingTypeId.PER_HEAD]: "$/Unit",
  [PricingTypeId.GROSS]: "$/Gross",
} as const satisfies Partial<Record<PricingTypeId, string>>;

export const pricingTypeString =
  (
    pricingTypeDisplayNameLookup: Partial<
      Record<PricingTypeId, string>
    > = LivestockSalePricingTypeDisplayNameLkp,
  ) =>
  (pricingTypeId: PricingTypeId): string => {
    const displayName = pricingTypeDisplayNameLookup[pricingTypeId];
    if (displayName) {
      return displayName;
    } else {
      throw new Error("Not a pricing type");
    }
  };

export const formatPricingTypeOutput = (
  id: PricingTypeId,
  price: number | string,
): string => {
  switch (id) {
    case PricingTypeId.PER_HEAD:
      return `$${price}/Head`;
    case PricingTypeId.PER_KILO:
      return `$${price}/kg`;
    case PricingTypeId.GROSS:
      return `$${price} Gross`;
    default:
      throw new Error("Not a pricing type");
  }
};

export const pricingTypeToString = pricingTypeString();

export const allPricingTypeOptions = () =>
  getPropertyList(ALL_PRICING_TYPES, pricingTypeString());

export const clearingSalePricingTypeOptions = () =>
  getPropertyList(
    CLEARING_SALE_PRICING_TYPES,
    pricingTypeString(ClearingSalePricingTypeDisplayNameLkp),
  );

export function getReceiptPricingTypeString(
  pricingTypeId: PricingTypeId,
): string | undefined {
  if (pricingTypeId === PricingTypeId.PER_HEAD) {
    return "$/Hd";
  } else if (pricingTypeId === PricingTypeId.PER_KILO) {
    return "c/kg";
  } else if (pricingTypeId === PricingTypeId.GROSS) {
    return "$ Gross";
  }
  return undefined;
}

export function getReceiptPriceString(
  unitPrice: number | string,
  pricingTypeId: PricingTypeId,
  defaultPricingTypeId?: PricingTypeId,
): string | undefined {
  if (
    defaultPricingTypeId === undefined ||
    pricingTypeId === defaultPricingTypeId
  ) {
    if (
      pricingTypeId === PricingTypeId.PER_HEAD ||
      pricingTypeId === PricingTypeId.GROSS
    ) {
      return `$${unitPrice}`;
    } else if (pricingTypeId === PricingTypeId.PER_KILO) {
      return `${unitPrice}c`;
    }
  } else if (pricingTypeId === PricingTypeId.PER_HEAD) {
    return `$${unitPrice}/Hd`;
  } else if (pricingTypeId === PricingTypeId.PER_KILO) {
    return `${unitPrice}c/kg`;
  } else if (pricingTypeId === PricingTypeId.GROSS) {
    return `$${unitPrice} Gross`;
  }
  return undefined;
}
